import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaShoppingCart } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { productDetailPath } from "../../constants/routes";
import { useLocalizedProduct } from "../../hooks/useLocalizedProduct";
import { useCartStore } from "../../store/cartStore";
import ProductSummary from "./ProductSummary";
import ProductColorSwatches from "./ProductColorSwatches";
import "./ProductCard.css";

function ProductCard({ product, showAddToCart = false }) {
  const { t } = useTranslation();
  const localizedProduct = useLocalizedProduct(product);
  const addItem = useCartStore((state) => state.addItem);
  const [added, setAdded] = useState(false);
  const hasColors = localizedProduct?.colors?.length > 0;
  const [selectedColor, setSelectedColor] = useState(localizedProduct?.colors?.[0] || "");

  if (!localizedProduct) return null;

  const detailPath = productDetailPath(localizedProduct.slug);
  const canPurchase = localizedProduct.purchasable && localizedProduct.inStock;

  const handleAddToCart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canPurchase) return;
    if (hasColors && !selectedColor) return;

    addItem(localizedProduct, 1, hasColors ? { color: selectedColor } : {});
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2000);
  };

  return (
    <article className="caf-product-card">
      <Link to={detailPath} className="caf-product-card__link">
        <div className="caf-product-card__media">
          {localizedProduct.image ? (
            <img
              src={localizedProduct.image}
              alt={localizedProduct.name}
              className="caf-product-card__image"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="caf-product-card__placeholder" aria-hidden="true" />
          )}
        </div>

        <div className="caf-product-card__body">
          <ProductSummary product={localizedProduct} titleTag="h3" />
        </div>
      </Link>

      <div className="caf-product-card__footer">
        <div className="caf-product-card__color-slot" aria-hidden={!hasColors}>
          {hasColors ? (
            <ProductColorSwatches
              colors={localizedProduct.colors}
              selected={selectedColor}
              onChange={setSelectedColor}
              compact
              hideLabel
              className="caf-product-card__colors"
            />
          ) : null}
        </div>

        {showAddToCart ? (
          <button
            type="button"
            className="caf-product-card__btn"
            onClick={handleAddToCart}
            disabled={!canPurchase || (hasColors && !selectedColor)}
          >
            <FaShoppingCart className="caf-product-card__btn-icon" aria-hidden="true" />
            <span>{added ? t("Added to cart") : t("Add to cart")}</span>
          </button>
        ) : (
          <Link to={detailPath} className="caf-product-card__btn caf-product-card__btn--outline">
            {t("View product")}
          </Link>
        )}
      </div>
    </article>
  );
}

export default ProductCard;
